import React, { useState } from 'react';
import './WelcomeScreen.css';

const API = 'https://editor.bandeleart.ch';
const PRIVACY_URL = 'https://bandeleart.ch/datenschutzbestimmungen';

const STEPS = {
  HOME: 'home',
  NEW_EMAIL: 'new_email',
  NEW_CODE: 'new_code',
  LOGIN_EMAIL: 'login_email',
  LOGIN_CODE: 'login_code',
  RECOVER: 'recover',
  RECOVER_SENT: 'recover_sent',
};

function PrivacyConsent({ checked, onChange }) {
  return (
    <div style={{ margin: '12px 0', padding: '12px 14px', background: '#f5f7ff', borderRadius: 8, fontSize: 14, lineHeight: 1.5 }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          Ich habe die{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Datenschutzbestimmungen</a>{' '}
          gelesen und bin damit einverstanden. Ich erfasse nur Daten und Fotos von Personen,
          für die ich die Erlaubnis habe.
        </span>
      </label>
    </div>
  );
}

function StepIndicator({ steps, current }) {
  return (
    <div className="ws-steps">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className={`ws-step ${i === current ? 'ws-step--active' : i < current ? 'ws-step--done' : ''}`}>
            {i < current ? '✓' : i + 1}
          </div>
          {i < steps.length - 1 && <div className={`ws-step-line ${i < current ? 'ws-step-line--done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export function WelcomeScreen({ onStart, onLogin, onCancel, initialStep, userEmail, userToken, magicLogin, prefillEmail }) {
  // Eingeloggt (vom Dashboard): neuer Stammbaum nur mit Namen, ohne E-Mail + Code
  const loggedIn = !!userToken;
  const [step, setStep] = useState(magicLogin ? STEPS.LOGIN_CODE : prefillEmail ? STEPS.LOGIN_EMAIL : (initialStep || STEPS.HOME));
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState(magicLogin?.code || '');
  const [loginEmail, setLoginEmail] = useState(magicLogin?.email || prefillEmail || '');
  const [recoverEmail, setRecoverEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [isNewLogin, setIsNewLogin] = useState(false); // „Neues Login erstellen“ statt „Ich habe bereits ein Login“
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const clearError = () => setError('');

  // ── Neuer Stammbaum ──────────────────────────────────────────────────

  const handleNewEmailSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!name.trim()) { setError('Bitte einen Namen für den Stammbaum eingeben.'); return; }
    if (loggedIn) {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/user/tree/new`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userToken}` },
          body: JSON.stringify({ name }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) { setError('Dein Login ist abgelaufen. Bitte melde dich neu an.'); return; }
        if (!res.ok) { setError(data.error || 'Fehler beim Erstellen.'); return; }
        onStart({ isNew: true, email: userEmail, treeCode: data.treeCode, treeName: data.treeName || name, keepLoginTime: true });
      } catch {
        setError('Server nicht erreichbar. Bitte später versuchen.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!email.includes('@')) { setError('Bitte eine gültige E-Mail-Adresse eingeben.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Fehler beim Senden.'); return; }
      setStep(STEPS.NEW_CODE);
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewCodeSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (code.length !== 6) { setError('Bitte den 6-stelligen Code eingeben.'); return; }
    if (needsConsent && !privacyAccepted) { setError('Bitte bestätige die Datenschutzbestimmungen.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, name, privacyAccepted }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.needsConsent) { setNeedsConsent(true); setError('Bitte bestätige noch die Datenschutzbestimmungen.'); return; }
        setError(data.error || 'Ungültiger Code.'); return;
      }
      onStart({ isNew: true, email, treeCode: data.treeCode, treeName: data.treeName || name });
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const resendNewCode = async () => {
    setCode('');
    setLoading(true);
    try {
      await fetch(`${API}/api/auth/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Login (bestehender Account) ──────────────────────────────────────

  const handleLoginEmailSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!loginEmail.includes('@')) { setError('Bitte eine gültige E-Mail-Adresse eingeben.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Fehler beim Senden.'); return; }
      setStep(STEPS.LOGIN_CODE);
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    clearError();
    if (code.length !== 6) { setError('Bitte den 6-stelligen Code eingeben.'); return; }
    if (needsConsent && !privacyAccepted) { setError('Bitte bestätige die Datenschutzbestimmungen.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, code, privacyAccepted }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.needsConsent) { setNeedsConsent(true); setError('Bitte bestätige noch die Datenschutzbestimmungen.'); return; }
        setError(data.error || 'Ungültiger Code.'); return;
      }
      onLogin({ email: loginEmail, token: data.token, trees: data.trees || [] });
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // „Direkt einloggen“ aus der Mail: Code automatisch prüfen (einmalig)
  const magicTriedRef = React.useRef(false);
  React.useEffect(() => {
    if (magicLogin && !magicTriedRef.current) {
      magicTriedRef.current = true;
      handleLoginCodeSubmit();
    }
  }, []); // eslint-disable-line

  const resendLoginCode = async () => {
    setCode('');
    setLoading(true);
    try {
      await fetch(`${API}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Code wiederherstellen ─────────────────────────────────────────────

  const handleRecoverSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!recoverEmail.includes('@')) { setError('Bitte eine gültige E-Mail-Adresse eingeben.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoverEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Fehler.'); return; }
      setStep(STEPS.RECOVER_SENT);
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (step === STEPS.HOME) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <div className="ws-logo">Stammbaum-Memo</div>
          <p className="ws-subtitle">Erstelle deinen persönlichen Familien-Stammbaum</p>
          <div className="ws-options">
            <button className="ws-option ws-option--primary" onClick={() => { setIsNewLogin(true); setStep(STEPS.LOGIN_EMAIL); clearError(); setCode(''); }}>
              <span className="ws-option-icon">✦</span>
              <span className="ws-option-title">Neues Login erstellen</span>
              <span className="ws-option-desc">Melde dich mit deiner E-Mail-Adresse an und erstelle danach deinen Stammbaum</span>
            </button>
            {onLogin && (
              <button className="ws-option" onClick={() => { setIsNewLogin(false); setStep(STEPS.LOGIN_EMAIL); clearError(); setCode(''); }}>
                <span className="ws-option-icon">↗</span>
                <span className="ws-option-title">Ich habe bereits ein Login</span>
                <span className="ws-option-desc">Öffne deine Stammbäume mit deiner E-Mail-Adresse</span>
              </button>
            )}
          </div>
          {onCancel && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="ws-btn ws-btn--secondary" onClick={onCancel}>← Zurück zu meinen Stammbäumen</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === STEPS.NEW_EMAIL) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          {!loggedIn && <StepIndicator steps={['E-Mail', 'Bestätigung']} current={0} />}
          <h2 className="ws-title">Neuer Stammbaum</h2>
          {loggedIn ? (
            <p className="ws-desc">
              Wie soll dein neuer Stammbaum heissen? Er wird mit <strong>{userEmail}</strong> verknüpft.
            </p>
          ) : (
            <p className="ws-desc">
              Gib deine E-Mail-Adresse ein. Wir senden dir einen Bestätigungscode
              sowie deinen persönlichen Stammbaum-Code, mit dem du deinen Stammbaum
              später wieder öffnen kannst.
            </p>
          )}
          <form onSubmit={handleNewEmailSubmit}>
            <input
              className="ws-input"
              type="text"
              placeholder="Name des Stammbaums (z.B. Familie Müller)"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
            {!loggedIn && (
              <input
                className="ws-input"
                type="email"
                placeholder="deine@email.ch"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            )}
            {error && <p className="ws-error">{error}</p>}
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => (loggedIn && onCancel ? onCancel() : setStep(STEPS.HOME))}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loggedIn
                  ? (loading ? 'Wird erstellt…' : 'Stammbaum erstellen')
                  : (loading ? 'Wird gesendet…' : 'Code senden')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.NEW_CODE) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <StepIndicator steps={['E-Mail', 'Bestätigung']} current={1} />
          <h2 className="ws-title">Code bestätigen</h2>
          <p className="ws-desc">
            Wir haben einen 6-stelligen Bestätigungscode an <strong>{email}</strong> gesendet.
            Bitte gib ihn hier ein.
          </p>
          <form onSubmit={handleNewCodeSubmit}>
            <input
              className="ws-input ws-input--code"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {needsConsent && <PrivacyConsent checked={privacyAccepted} onChange={v => { setPrivacyAccepted(v); if (v) clearError(); }} />}
            {error && <p className="ws-error">{error}</p>}
            <p className="ws-hint" onClick={resendNewCode} style={{cursor:'pointer'}}>
              Keinen Code erhalten? Erneut senden
            </p>
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.NEW_EMAIL)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird geprüft…' : 'Bestätigen & starten'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.LOGIN_EMAIL) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <StepIndicator steps={['E-Mail', 'Bestätigung']} current={0} />
          <h2 className="ws-title">{isNewLogin ? 'Neues Login erstellen' : 'Einloggen'}</h2>
          <p className="ws-desc">
            {isNewLogin
              ? 'Gib deine E-Mail-Adresse ein. Wir senden dir einen Bestätigungscode. Danach kannst du deinen ersten Stammbaum erstellen.'
              : 'Gib deine E-Mail-Adresse ein. Wir senden dir einen Einmalcode zum Einloggen.'}
          </p>
          <form onSubmit={handleLoginEmailSubmit}>
            <input
              className="ws-input"
              type="email"
              placeholder="deine@email.ch"
              value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)}
              autoFocus
            />
            {error && <p className="ws-error">{error}</p>}
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.HOME)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Code senden'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.LOGIN_CODE) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <StepIndicator steps={['E-Mail', 'Bestätigung']} current={1} />
          <h2 className="ws-title">Code bestätigen</h2>
          <p className="ws-desc">
            Wir haben einen 6-stelligen Einmalcode an <strong>{loginEmail}</strong> gesendet.
          </p>
          <form onSubmit={handleLoginCodeSubmit}>
            <input
              className="ws-input ws-input--code"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {needsConsent && <PrivacyConsent checked={privacyAccepted} onChange={v => { setPrivacyAccepted(v); if (v) clearError(); }} />}
            {error && <p className="ws-error">{error}</p>}
            <p className="ws-hint" onClick={resendLoginCode} style={{cursor:'pointer'}}>
              Keinen Code erhalten? Erneut senden
            </p>
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.LOGIN_EMAIL)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird geprüft…' : (isNewLogin ? 'Login erstellen' : 'Einloggen')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.RECOVER) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <h2 className="ws-title">Code vergessen</h2>
          <p className="ws-desc">
            Gib deine E-Mail-Adresse ein. Wir senden dir eine Liste aller Stammbäume
            die mit dieser Adresse verknüpft sind.
          </p>
          <form onSubmit={handleRecoverSubmit}>
            <input
              className="ws-input"
              type="email"
              placeholder="deine@email.ch"
              value={recoverEmail}
              onChange={e => setRecoverEmail(e.target.value)}
              autoFocus
            />
            {error && <p className="ws-error">{error}</p>}
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.HOME)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Codes anfordern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.RECOVER_SENT) {
    return (
      <div className="ws-overlay">
        <div className="ws-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✉️</div>
          <h2 className="ws-title">E-Mail gesendet</h2>
          <p className="ws-desc">
            Wir haben eine E-Mail mit allen deinen Stammbaum-Codes an <strong>{recoverEmail}</strong> gesendet.
          </p>
          <div className="ws-actions" style={{ justifyContent: 'center' }}>
            <button className="ws-btn ws-btn--primary" onClick={() => setStep(STEPS.HOME)}>
              Zurück zur Startseite
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
